// src/pages/Accounting/Receipts/components/ReviewMode/ReceiptPaper.tsx

import type React from "react";
import type { Receipt } from "../../types";
import { PALETTE, SOURCE_LABELS } from "../../constants";
import { formatDateLong, totalTaxForReceipt } from "../../utils";

interface ReceiptPaperProps {
  receipt: Receipt;
}

export function ReceiptPaper({ receipt }: ReceiptPaperProps) {
  const subtotal = receipt.total - totalTaxForReceipt(receipt.tax);
  const taxEntries: Array<[string, number]> = [];
  if (receipt.tax.gst) taxEntries.push(["GST (5%)", receipt.tax.gst]);
  if (receipt.tax.hst) taxEntries.push(["HST (13%)", receipt.tax.hst]);
  if (receipt.tax.pst) taxEntries.push(["PST", receipt.tax.pst]);
  if (receipt.tax.other) taxEntries.push(["Other tax", receipt.tax.other]);

  return (
    <div
      style={{
        padding: "20px 24px",
        overflowY: "auto",
        borderRight: `1px solid ${PALETTE.line}`,
        background: "#F6F7F7",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {receipt.imageUrl ? (
        <img
          src={receipt.imageUrl}
          alt={`Receipt from ${receipt.vendorName}`}
          style={{
            maxWidth: 400,
            width: "100%",
            borderRadius: 2,
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          }}
        />
      ) : (
        <div
          style={{
            background: "#FEFCF7",
            padding: "24px 22px",
            fontFamily: "'Courier New', Menlo, monospace",
            fontSize: 11,
            color: PALETTE.ink2,
            lineHeight: 1.6,
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            width: "100%",
            maxWidth: 360,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.1em",
              marginBottom: 2,
              color: PALETTE.ink,
              textTransform: "uppercase",
            }}
          >
            {receipt.vendorName || "Unknown vendor"}
          </div>
          <div style={row}>
            <span>{formatDateLong(receipt.date)}</span>
          </div>
          <div style={sep} />
          {receipt.lineItems && receipt.lineItems.length > 0 ? (
            <>
              {receipt.lineItems.map((item, i) => (
                <div key={i} style={row}>
                  <span>{item.name}</span>
                  <span>{item.amount.toFixed(2)}</span>
                </div>
              ))}
              <div style={sep} />
            </>
          ) : null}
          <div style={row}>
            <span>Subtotal</span>
            <span>{subtotal.toFixed(2)}</span>
          </div>
          {taxEntries.map(([label, amount]) => (
            <div key={label} style={row}>
              <span>{label}</span>
              <span>{amount.toFixed(2)}</span>
            </div>
          ))}
          <div
            style={{
              ...row,
              fontWeight: 700,
              fontSize: 11.5,
              color: PALETTE.ink,
              marginTop: 4,
            }}
          >
            <span>TOTAL</span>
            <span>{receipt.total.toFixed(2)}</span>
          </div>
        </div>
      )}
      <div
        style={{
          fontSize: 11.5,
          color: PALETTE.ink3,
          marginTop: 10,
          fontWeight: 500,
          textAlign: "center",
        }}
      >
        Receipt {SOURCE_LABELS[receipt.source]}
      </div>
    </div>
  );
}

const row: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  padding: "2px 0",
};

const sep: React.CSSProperties = {
  borderTop: `1px dashed ${PALETTE.ink4}`,
  margin: "8px 0",
};